import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { ShoppingBag, Menu, X, ChevronRight, Minus, Plus, Trash2, ArrowRight, Star, Heart, MapPin, Package, Truck, Shield, Leaf } from "lucide-react";
import { products, featuredProducts } from "./data/products";

// Cart Context
const CartContext = createContext();

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('valore_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  const [allProducts] = useState(products);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('valore_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (productId, quantity = 1) => {
    const existingItem = cart.find(item => item.product.id === productId);
    const product = allProducts.find(p => p.id === productId);
    
    if (!product) {
      toast.error('Product not found');
      return;
    }

    if (existingItem) {
      setCart(cart.map(item => 
        item.product.id === productId 
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ));
    } else {
      setCart([...cart, { id: `cart_${Date.now()}`, product, quantity }]);
    }
    
    toast.success('Added to cart');
  };

  const updateCartItem = (itemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    ));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
    toast.success('Removed from cart');
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ 
      cart, 
      cartTotal, 
      cartCount, 
      addToCart, 
      updateCartItem, 
      removeFromCart, 
      clearCart,
      allProducts
    }}>
      {children}
    </CartContext.Provider>
  );
};

const useCart = () => useContext(CartContext);

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { cart, cartCount } = useCart();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-ivory/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`} data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="font-playfair text-3xl font-semibold text-charcoal tracking-wide">Valore</span>
          </Link>

          <div className="hidden md:flex items-center space-x-10">
            <Link to="/" className="nav-link" data-testid="nav-home">Home</Link>
            <Link to="/products" className="nav-link" data-testid="nav-shop">Shop</Link>
            <Link to="/about" className="nav-link" data-testid="nav-about">About</Link>
            <Link to="/contact" className="nav-link" data-testid="nav-contact">Contact</Link>
          </div>

          <div className="flex items-center space-x-4">
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="relative p-2 hover:bg-gold/10" data-testid="cart-button">
                  <ShoppingBag className="h-5 w-5 text-charcoal" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gold text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                      {cartCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="bg-ivory border-l border-beige/30 w-full sm:max-w-md" data-testid="cart-sheet">
                <SheetHeader>
                  <SheetTitle className="font-playfair text-2xl text-charcoal">Your Cart</SheetTitle>
                </SheetHeader>
                <CartSidebar onClose={() => setCartOpen(false)} />
              </SheetContent>
            </Sheet>

            <Button variant="ghost" className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-ivory/98 backdrop-blur-lg border-t border-beige/20" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            <Link to="/" className="block py-2 text-charcoal font-medium" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/products" className="block py-2 text-charcoal font-medium" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
            <Link to="/about" className="block py-2 text-charcoal font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
            <Link to="/contact" className="block py-2 text-charcoal font-medium" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Cart Sidebar
const CartSidebar = ({ onClose }) => {
  const { cart, cartTotal, updateCartItem, removeFromCart } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20" data-testid="empty-cart">
        <ShoppingBag className="h-16 w-16 text-beige mb-4" />
        <p className="text-charcoal/60 font-light">Your cart is empty</p>
        <Link to="/products">
          <Button className="mt-6 btn-primary">Explore Products</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="cart-content">
      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {cart.map((item) => (
          <div key={item.id} className="flex gap-4" data-testid={`cart-item-${item.id}`}>
            <img src={item.product.image} alt={item.product.name} className="w-20 h-20 object-cover rounded-lg" />
            <div className="flex-1">
              <h4 className="font-medium text-charcoal text-sm">{item.product.name}</h4>
              <p className="text-gold font-semibold mt-1">₹{item.product.price.toFixed(2)}</p>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => updateCartItem(item.id, item.quantity - 1)} className="p-1 hover:bg-beige/30 rounded" data-testid={`decrease-qty-${item.id}`}>
                  <Minus className="h-3 w-3" />
                </button>
                <span className="text-sm w-6 text-center">{item.quantity}</span>
                <button onClick={() => updateCartItem(item.id, item.quantity + 1)} className="p-1 hover:bg-beige/30 rounded" data-testid={`increase-qty-${item.id}`}>
                  <Plus className="h-3 w-3" />
                </button>
                <button onClick={() => removeFromCart(item.id)} className="ml-auto p-1 hover:bg-red-50 text-red-500 rounded" data-testid={`remove-item-${item.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-beige/30 pt-6 pb-6 space-y-4">
        <div className="flex justify-between text-charcoal">
          <span>Subtotal</span>
          <span className="font-semibold">₹{cartTotal.toFixed(2)}</span>
        </div>
        <p className="text-sm text-charcoal/60">Shipping calculated at checkout</p>
        <Link to="/checkout" className="block" onClick={onClose}>
          <Button className="w-full btn-primary" data-testid="checkout-btn">Proceed to Checkout</Button>
        </Link>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = () => (
  <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-gradient-to-b from-ivory via-ivory/95 to-ivory" />
    <div className="absolute inset-0 jute-texture opacity-40" />

    {/* Baskets prop bottom-right, slightly smaller */}
    <div className="pointer-events-none absolute inset-0 flex justify-end items-end pr-6 md:pr-16 pb-4 md:pb-8">
      <img
        src="/assets/baskets.png"
        alt="Woven baskets decorative"
        className="h-36 md:h-48 lg:h-56 object-contain opacity-25 md:opacity-35 mix-blend-multiply drop-shadow-[0_10px_25px_rgba(0,0,0,0.18)] transition-all duration-500 ease-out"
      />
    </div>


    <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
      <div className="animate-fade-in-up">
        <span className="inline-block text-gold font-medium tracking-[0.35em] uppercase text-sm md:text-base mb-8 mt-4">Artisan Excellence</span>
        <h1 className="font-playfair text-5xl md:text-7xl lg:text-7xl xl:text-8xl text-charcoal font-light leading-[1.15] mb-10">
          <span className="animate-subtle-glow inline-block mb-2">Crafted by Heart,</span><br />
          <span className="italic text-gold relative inline-block">
            <span className="absolute inset-0 bg-gradient-to-r from-gold/0 via-gold/5 to-gold/0 -z-10"></span>
            Perfected by Hand
          </span>
        </h1>
        <p className="text-charcoal/70 text-lg md:text-xl max-w-2xl mx-auto mb-14 font-light leading-relaxed">
          Discover a curated collection of handcrafted treasures, where every piece tells a story of tradition, passion, and timeless artistry.
        </p>
        <div className="flex flex-col sm:flex-row gap-5 justify-center">
          <Link to="/products" className="group">
            <Button 
              className="btn-primary text-lg px-12 py-6 transform transition-all duration-300 hover:scale-105 hover:shadow-lg" 
              data-testid="hero-shop-btn"
            >
              <span className="flex items-center group-hover:translate-x-1 transition-transform duration-300">
                Explore Collection <ArrowRight className="ml-3 h-5 w-5" />
              </span>
            </Button>
          </Link>
          <Link to="/about">
            <Button variant="outline" className="btn-secondary text-lg px-10 py-6" data-testid="hero-story-btn">
              Our Story
            </Button>
          </Link>
        </div>
      </div>
    </div>

    {/* Scroll Indicator */}
    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
      <div className="w-6 h-10 border-2 border-charcoal/30 rounded-full flex items-start justify-center p-2">
        <div className="w-1.5 h-3 bg-gold rounded-full animate-scroll" />
      </div>
    </div>
  </section>
);

// Featured Products
const FeaturedProducts = ({ products }) => (
  <section className="py-24 bg-ivory relative" data-testid="featured-products">
    <div className="absolute inset-0 jute-texture opacity-20" />
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="text-center mb-16 animate-fade-in">
        <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">Handpicked Selection</span>
        <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mt-4">Featured Artisan Pieces</h2>
        <div className="w-24 h-0.5 bg-gold mx-auto mt-6" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.slice(0, 6).map((product, index) => (
          <ProductCard key={product.id} product={product} index={index} />
        ))}
      </div>

      <div className="text-center mt-16">
        <Link to="/products">
          <Button variant="outline" className="btn-secondary text-lg px-12 py-6" data-testid="view-all-products-btn">
            View All Collection <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Product Card
const ProductCard = ({ product, index }) => {
  const { addToCart } = useCart();
  const [isHovered, setIsHovered] = useState(false);

  const handleWishlist = () => {
    toast.info('Added to wishlist');
  };

  return (
    <div
      className="group product-card animate-fade-in-up"
      style={{ animationDelay: `${index * 100}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden rounded-2xl bg-beige/20 mb-5">
        <Link to={`/product/${product.id}`}>
          <img
            src={product.image}
            alt={product.name}
            className={`w-full aspect-square object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
          />
        </Link>
        
        {product.original_price && (
          <Badge className="absolute top-4 left-4 bg-gold text-white border-0">Sale</Badge>
        )}
        
        <div className={`absolute inset-0 bg-black/45 flex items-center justify-center transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex gap-3">
            <Button
              onClick={() => addToCart(product.id)}
              variant="outline"
              className="bg-white/90 text-charcoal border-gold/40 hover:bg-gold/90 hover:text-charcoal shadow-md"
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingBag className="h-4 w-4 mr-2" /> Add to Cart
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleWishlist}
              className="bg-white/80 text-charcoal hover:bg-gold/90 hover:text-charcoal rounded-full shadow-md"
              aria-label="Add to wishlist"
            >
              <Heart className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="px-1">
        <span className="text-gold text-sm font-medium tracking-wider uppercase">{product.category}</span>
        <Link to={`/product/${product.id}`}>
          <h3 className="font-playfair text-xl text-charcoal mt-2 hover:text-gold transition-colors">{product.name}</h3>
        </Link>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-charcoal font-semibold text-lg">₹{product.price.toFixed(2)}</span>
          {product.original_price && (
            <span className="text-charcoal/40 line-through">₹{product.original_price.toFixed(2)}</span>
          )}
        </div>
        {product.artisan && (
          <p className="text-charcoal/60 text-sm mt-2 flex items-center">
            <MapPin className="h-3 w-3 mr-1" /> {product.artisan.location}
          </p>
        )}
      </div>
    </div>
  );
};

// Value Propositions
const ValueProps = () => (
  <section className="py-20 bg-gradient-to-r from-ivory via-beige/40 to-ivory text-charcoal relative overflow-hidden" data-testid="value-props">
    <div className="absolute inset-0 jute-texture opacity-20" />
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        {[
          { icon: Package, title: 'Handcrafted', desc: 'Each piece made with care and precision' },
          { icon: Truck, title: 'Free Shipping', desc: 'On orders over $200 worldwide' },
          { icon: Shield, title: 'Authentic', desc: 'Certified artisan craftsmanship' },
          { icon: Leaf, title: 'Sustainable', desc: 'Ethically sourced materials' },
        ].map((item, i) => (
          <div key={i} className="text-center group">
            <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/20 border border-gold/40 transition-colors">
              <item.icon className="h-7 w-7 text-gold" />
            </div>
            <h3 className="font-playfair text-xl text-charcoal mb-2 tracking-wide">{item.title}</h3>
            <p className="text-charcoal/70 text-sm font-light tracking-wide">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// About Preview
const AboutPreview = () => (
  <section className="py-24 bg-ivory relative" data-testid="about-preview">
    <div className="absolute inset-0 jute-texture opacity-20" />
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div className="relative">
          <div className="absolute -top-8 -left-8 w-full h-full border-2 border-gold/30 rounded-3xl" />
          <img
            src="https://images.unsplash.com/photo-1590605103416-230704277b05?w=800"
            alt="Artisan at work"
            className="rounded-3xl shadow-2xl relative z-10 w-full"
          />
        </div>
        
        <div className="animate-fade-in-right">
          <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">Our Heritage</span>
          <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mt-4 mb-6">Where Tradition Meets Elegance</h2>
          <p className="text-charcoal/70 leading-relaxed mb-6">
            At Valore, we believe in the extraordinary power of human hands. Each piece in our collection represents hours of dedicated craftsmanship, carrying forward traditions that span generations.
          </p>
          <p className="text-charcoal/70 leading-relaxed mb-8">
            We partner directly with artisans from around the world, ensuring fair compensation while preserving ancient techniques that might otherwise be lost to time.
          </p>
          <Link to="/about">
            <Button className="btn-primary" data-testid="learn-more-btn">
              Learn Our Story <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-gradient-to-r from-ivory via-beige/40 to-ivory text-charcoal py-20 relative border-t border-gold/20" data-testid="footer">
    <div className="absolute inset-0 jute-texture opacity-10" />
    <div className="max-w-7xl mx-auto px-6 relative z-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="col-span-1 md:col-span-2">
          <span className="font-playfair text-3xl text-charcoal">Valore</span>
          <p className="text-charcoal/70 mt-4 max-w-md leading-relaxed">
            Curating the finest handcrafted treasures from artisans around the world. Every piece tells a story of tradition, passion, and timeless artistry.
          </p>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4 text-charcoal">Quick Links</h4>
          <div className="space-y-3">
            <Link to="/products" className="block text-charcoal/70 hover:text-gold transition-colors">Shop All</Link>
            <Link to="/about" className="block text-charcoal/70 hover:text-gold transition-colors">Our Story</Link>
            <Link to="/contact" className="block text-charcoal/70 hover:text-gold transition-colors">Contact Us</Link>
          </div>
        </div>
        
        <div>
          <h4 className="font-semibold mb-4 text-charcoal">Contact</h4>
          <div className="space-y-3 text-charcoal/70">
            <p>hello@valore.com</p>
            <p>+1 (555) 123-4567</p>
            <p>Mon - Fri: 9am - 6pm</p>
          </div>
        </div>
      </div>
      
      <Separator className="my-12 bg-beige/60" />
      
      <div className="flex flex-col md:flex-row justify-between items-center text-charcoal/50 text-sm">
        <p>&copy; 2025 Valore. All rights reserved.</p>
        <div className="flex gap-6 mt-4 md:mt-0">
          <span className="hover:text-gold cursor-pointer transition-colors">Privacy Policy</span>
          <span className="hover:text-gold cursor-pointer transition-colors">Terms of Service</span>
        </div>
      </div>
    </div>
  </footer>
);

// Home Page
const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        // Then fetch products
        const response = await axios.get(`${API}/products?featured=true`);
        setProducts(response.data);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <main data-testid="home-page">
      <HeroSection />
      <ValueProps />
      {!loading && <FeaturedProducts products={products} />}
      <AboutPreview />
    </main>
  );
};

// Products Page
const ProductsPage = () => {
  const { allProducts } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  const filteredProducts = selectedCategory === 'all' 
    ? allProducts 
    : allProducts.filter(p => p.category === selectedCategory);

  return (
    <main className="pt-24 min-h-screen bg-ivory" data-testid="products-page">
      <div className="absolute inset-0 jute-texture opacity-20 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        <div className="text-center mb-12">
          <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">Our Collection</span>
          <h1 className="font-playfair text-4xl md:text-5xl text-charcoal mt-4">Artisan Treasures</h1>
          <div className="w-24 h-0.5 bg-gold mx-auto mt-6" />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            className={selectedCategory === 'all' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setSelectedCategory('all')}
            data-testid="filter-all"
          >
            All
          </Button>
          {['pottery', 'textiles', 'woodworking', 'jewelry', 'paper', 'glass', 'metal', 'baskets'].map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              className={selectedCategory === cat ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setSelectedCategory(cat)}
              data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
            >
              {cat === 'baskets' ? 'Baskets' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="animate-pulse">
                <div className="bg-beige/30 aspect-square rounded-2xl mb-4" />
                <div className="h-4 bg-beige/30 rounded w-1/4 mb-2" />
                <div className="h-6 bg-beige/30 rounded w-3/4 mb-2" />
                <div className="h-4 bg-beige/30 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-charcoal/60">No products found in this category.</p>
          </div>
        )}
      </div>
    </main>
  );
};

// Product Detail Page
const ProductDetailPage = () => {
  const { id } = useParams();
  const { allProducts, addToCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  
  // Find the product from our mock data
  const product = allProducts.find(p => p.id === id);
  
  // Get related products (for demo, just get other products in the same category)
  const relatedProducts = allProducts
    .filter(p => p.id !== id && p.category === product?.category)
    .slice(0, 4);
    
  // Redirect to 404 if product not found
  useEffect(() => {
    if (!product && !loading) {
      navigate('/404');
    }
  }, [product, loading, navigate]);

  if (loading || !product) {
    return (
      <main className="pt-24 min-h-screen bg-ivory">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="bg-beige/30 aspect-square rounded-2xl" />
            <div className="space-y-4">
              <div className="h-4 bg-beige/30 rounded w-1/4" />
              <div className="h-10 bg-beige/30 rounded w-3/4" />
              <div className="h-6 bg-beige/30 rounded w-1/3" />
              <div className="h-32 bg-beige/30 rounded" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="pt-24 min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-playfair text-2xl text-charcoal mb-4">Product Not Found</h2>
          <Link to="/products"><Button className="btn-primary">Back to Shop</Button></Link>
        </div>
      </main>
    );
  }

  const allImages = [product.image, ...(product.images || [])];

  return (
    <main className="pt-24 min-h-screen bg-ivory relative" data-testid="product-detail-page">
      <div className="absolute inset-0 jute-texture opacity-20 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative overflow-hidden rounded-2xl bg-beige/20">
              <img
                src={allImages[selectedImage]}
                alt={product.name}
                className="w-full aspect-square object-cover"
              />
              {product.original_price && (
                <Badge className="absolute top-4 left-4 bg-gold text-white border-0">Sale</Badge>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-3">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${selectedImage === i ? 'border-gold' : 'border-transparent'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="animate-fade-in-right">
            <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">{product.category}</span>
            <h1 className="font-playfair text-4xl text-charcoal mt-2 mb-4">{product.name}</h1>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-3xl text-charcoal font-semibold">₹{product.price.toFixed(2)}</span>
              {product.original_price && (
                <span className="text-xl text-charcoal/40 line-through">₹{product.original_price.toFixed(2)}</span>
              )}
            </div>

            <p className="text-charcoal/70 leading-relaxed mb-8">{product.description}</p>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center border border-beige rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-beige/30" data-testid="qty-decrease">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-beige/30" data-testid="qty-increase">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <Button onClick={() => addToCart(product.id, quantity)} className="btn-primary flex-1" data-testid="add-to-cart-detail">
                <ShoppingBag className="h-4 w-4 mr-2" /> Add to Cart
              </Button>
            </div>

            <Separator className="my-8 bg-beige/50" />

            {/* Product Details */}
            <div className="space-y-6">
              {product.materials?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-charcoal mb-2">Materials</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.materials.map((m, i) => (
                      <Badge key={i} variant="outline" className="border-gold/50 text-charcoal">{m}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {product.origin && (
                <div>
                  <h3 className="font-semibold text-charcoal mb-2">Origin</h3>
                  <p className="text-charcoal/70 flex items-center"><MapPin className="h-4 w-4 mr-2 text-gold" />{product.origin}</p>
                </div>
              )}

              {product.craftsmanship_details && (
                <div>
                  <h3 className="font-semibold text-charcoal mb-2">Craftsmanship</h3>
                  <p className="text-charcoal/70 leading-relaxed">{product.craftsmanship_details}</p>
                </div>
              )}
            </div>

            {/* Artisan Info */}
            {product.artisan && (
              <div className="mt-8 p-6 bg-beige/20 rounded-2xl">
                <h3 className="font-playfair text-xl text-charcoal mb-4">Meet the Artisan</h3>
                <div className="flex gap-4">
                  {product.artisan.image && (
                    <img src={product.artisan.image} alt={product.artisan.name} className="w-16 h-16 rounded-full object-cover" />
                  )}
                  <div>
                    <p className="font-semibold text-charcoal">{product.artisan.name}</p>
                    <p className="text-gold text-sm">{product.artisan.location}</p>
                    <p className="text-charcoal/70 text-sm mt-2">{product.artisan.bio}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cart, cartTotal, sessionId, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    notes: ''
  });

  const shipping = cartTotal >= 200 ? 0 : 15;
  const total = cartTotal + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/orders`, {
        ...formData,
        session_id: sessionId
      });
      setOrderId(response.data.id);
      setOrderComplete(true);
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (orderComplete) {
    return (
      <main className="pt-24 min-h-screen bg-ivory flex items-center justify-center" data-testid="order-confirmation">
        <div className="absolute inset-0 jute-texture opacity-20" />
        <div className="text-center relative z-10 max-w-lg mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
            <Package className="h-10 w-10 text-gold" />
          </div>
          <h1 className="font-playfair text-4xl text-charcoal mb-4">Thank You!</h1>
          <p className="text-charcoal/70 mb-6">Your order has been placed successfully. We'll send you an email confirmation shortly.</p>
          <p className="text-sm text-charcoal/50 mb-8">Order ID: {orderId}</p>
          <Link to="/products">
            <Button className="btn-primary">Continue Shopping</Button>
          </Link>
        </div>
      </main>
    );
  }

  if (cart.length === 0) {
    return (
      <main className="pt-24 min-h-screen bg-ivory flex items-center justify-center" data-testid="empty-checkout">
        <div className="text-center">
          <ShoppingBag className="h-16 w-16 text-beige mx-auto mb-4" />
          <h2 className="font-playfair text-2xl text-charcoal mb-4">Your cart is empty</h2>
          <Link to="/products"><Button className="btn-primary">Start Shopping</Button></Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 min-h-screen bg-ivory" data-testid="checkout-page">
      <div className="absolute inset-0 jute-texture opacity-20 pointer-events-none" />
      
      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10">
        <h1 className="font-playfair text-4xl text-charcoal mb-12 text-center">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Order Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-white/50 rounded-2xl p-8 border border-beige/30">
              <h2 className="font-playfair text-2xl text-charcoal mb-6">Shipping Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm text-charcoal/70 mb-1 block">Full Name *</label>
                  <Input
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="text-sm text-charcoal/70 mb-1 block">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="text-sm text-charcoal/70 mb-1 block">Phone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    data-testid="input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-charcoal/70 mb-1 block">Address *</label>
                  <Input
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    data-testid="input-address"
                  />
                </div>
                <div>
                  <label className="text-sm text-charcoal/70 mb-1 block">City *</label>
                  <Input
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    data-testid="input-city"
                  />
                </div>
                <div>
                  <label className="text-sm text-charcoal/70 mb-1 block">Postal Code *</label>
                  <Input
                    required
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    data-testid="input-postal"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-charcoal/70 mb-1 block">Country *</label>
                  <Select value={formData.country} onValueChange={(val) => setFormData({...formData, country: val})}>
                    <SelectTrigger className="bg-ivory border-beige" data-testid="select-country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="UK">United Kingdom</SelectItem>
                      <SelectItem value="DE">Germany</SelectItem>
                      <SelectItem value="FR">France</SelectItem>
                      <SelectItem value="AU">Australia</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm text-charcoal/70 mb-1 block">Order Notes</label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-ivory border-beige focus:border-gold"
                    placeholder="Special instructions for your order..."
                    data-testid="input-notes"
                  />
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full btn-primary text-lg py-6" disabled={loading || !formData.country} data-testid="place-order-btn">
              {loading ? 'Processing...' : `Place Order - $${total.toFixed(2)}`}
            </Button>
          </form>

          {/* Order Summary */}
          <div className="bg-white/50 rounded-2xl p-8 border border-beige/30 h-fit">
            <h2 className="font-playfair text-2xl text-charcoal mb-6">Order Summary</h2>
            
            <div className="space-y-4 mb-6">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <img src={item.product.image} alt={item.product.name} className="w-16 h-16 object-cover rounded-lg" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-charcoal">{item.product.name}</h4>
                    <p className="text-sm text-charcoal/60">Qty: {item.quantity}</p>
                  </div>
                  <span className="font-medium text-charcoal">${(item.product.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <Separator className="bg-beige/50 my-6" />

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-charcoal/70">Subtotal</span>
                <span className="text-charcoal">${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-charcoal/70">Shipping</span>
                <span className="text-charcoal">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
              </div>
              {cartTotal < 200 && (
                <p className="text-gold text-xs">Add ${(200 - cartTotal).toFixed(2)} more for free shipping</p>
              )}
            </div>

            <Separator className="bg-beige/50 my-6" />

            <div className="flex justify-between text-lg font-semibold">
              <span className="text-charcoal">Total</span>
              <span className="text-charcoal">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// About Page
const AboutPage = () => (
  <main className="pt-24 min-h-screen bg-ivory" data-testid="about-page">
    <div className="absolute inset-0 jute-texture opacity-20 pointer-events-none" />
    
    {/* Hero */}
    <section className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">Our Story</span>
        <h1 className="font-playfair text-5xl md:text-6xl text-charcoal mt-4 mb-6">The Art of Authentic Craftsmanship</h1>
        <p className="text-charcoal/70 text-lg max-w-3xl mx-auto leading-relaxed">
          Founded on the belief that true luxury lies in authenticity, Valore is dedicated to preserving and celebrating the world's finest artisan traditions.
        </p>
      </div>
    </section>

    {/* Mission */}
    <section className="py-20 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-full h-full border-2 border-gold/30 rounded-3xl" />
            <img
              src="https://images.unsplash.com/photo-1589051079002-b140a970f568?w=800"
              alt="Artisan crafting"
              className="rounded-3xl shadow-xl relative z-10 w-full"
            />
          </div>
          <div>
            <h2 className="font-playfair text-4xl text-charcoal mb-6">Our Mission</h2>
            <p className="text-charcoal/70 leading-relaxed mb-6">
              At Valore, we bridge the gap between master artisans and discerning collectors who appreciate the irreplaceable value of handcrafted excellence.
            </p>
            <p className="text-charcoal/70 leading-relaxed mb-6">
              Each piece in our collection represents not just an object, but a story — of ancient techniques passed through generations, of materials sourced with respect for nature, and of hands that have dedicated a lifetime to perfecting their craft.
            </p>
            <p className="text-charcoal/70 leading-relaxed">
              We work directly with artisans, ensuring fair compensation and sustainable practices while bringing their extraordinary creations to those who will treasure them.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-20 bg-gradient-to-r from-ivory via-beige/40 to-ivory relative">
      <div className="absolute inset-0 jute-texture opacity-10" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl text-charcoal">Our Values</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { title: 'Authenticity', desc: 'Every piece is genuinely handcrafted using traditional techniques, never mass-produced.' },
            { title: 'Sustainability', desc: 'We prioritize eco-conscious materials and support artisans who practice sustainable harvesting.' },
            { title: 'Fair Trade', desc: 'Our artisan partners receive fair compensation, empowering communities worldwide.' },
          ].map((v, i) => (
            <div key={i} className="text-center">
              <h3 className="font-playfair text-2xl text-gold mb-4">{v.title}</h3>
              <p className="text-charcoal/70 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Artisan Network */}
    <section className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">Global Network</span>
          <h2 className="font-playfair text-4xl text-charcoal mt-4">Our Artisan Partners</h2>
          <div className="w-24 h-0.5 bg-gold mx-auto mt-6" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {['Mexico', 'Italy', 'Japan', 'India', 'Morocco', 'Peru', 'Sweden', 'Palestine'].map((country, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-beige/10 hover:bg-beige/20 transition-colors">
              <p className="font-playfair text-lg text-charcoal">{country}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </main>
);

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="pt-24 min-h-screen bg-ivory flex items-center justify-center" data-testid="contact-success">
        <div className="absolute inset-0 jute-texture opacity-20" />
        <div className="text-center relative z-10 max-w-lg mx-auto px-6">
          <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
            <Star className="h-10 w-10 text-gold" />
          </div>
          <h1 className="font-playfair text-4xl text-charcoal mb-4">Thank You!</h1>
          <p className="text-charcoal/70 mb-8">We've received your message and will get back to you within 24-48 hours.</p>
          <Link to="/">
            <Button className="btn-primary">Return Home</Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 min-h-screen bg-ivory" data-testid="contact-page">
      <div className="absolute inset-0 jute-texture opacity-20 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 py-24 relative z-10">
        <div className="text-center mb-16">
          <span className="text-gold font-medium tracking-[0.2em] uppercase text-sm">Get in Touch</span>
          <h1 className="font-playfair text-5xl text-charcoal mt-4">Contact Us</h1>
          <div className="w-24 h-0.5 bg-gold mx-auto mt-6" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-white/50 rounded-3xl p-10 border border-beige/30">
            <h2 className="font-playfair text-2xl text-charcoal mb-6">Send us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-sm text-charcoal/70 mb-1 block">Name *</label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-ivory border-beige focus:border-gold"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-sm text-charcoal/70 mb-1 block">Email *</label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-ivory border-beige focus:border-gold"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="text-sm text-charcoal/70 mb-1 block">Subject *</label>
                <Select value={formData.subject} onValueChange={(val) => setFormData({...formData, subject: val})}>
                  <SelectTrigger className="bg-ivory border-beige" data-testid="contact-subject">
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                    <SelectItem value="Product Question">Product Question</SelectItem>
                    <SelectItem value="Order Support">Order Support</SelectItem>
                    <SelectItem value="Artisan Partnership">Artisan Partnership</SelectItem>
                    <SelectItem value="Wholesale">Wholesale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-charcoal/70 mb-1 block">Message *</label>
                <Textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="bg-ivory border-beige focus:border-gold"
                  placeholder="Tell us how we can help..."
                  data-testid="contact-message"
                />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={loading || !formData.subject} data-testid="contact-submit">
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h2 className="font-playfair text-2xl text-charcoal mb-4">Visit Us</h2>
              <p className="text-charcoal/70 leading-relaxed">
                Our flagship showroom is located in the heart of the design district, where you can experience our collection in person.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-charcoal">Address</h3>
                  <p className="text-charcoal/70">123 Artisan Way<br />Design District, NY 10001</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <Star className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-charcoal">Hours</h3>
                  <p className="text-charcoal/70">Mon - Fri: 10am - 7pm<br />Sat - Sun: 11am - 6pm</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center shrink-0">
                  <Package className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-charcoal">Contact</h3>
                  <p className="text-charcoal/70">hello@valore.com<br />+1 (555) 123-4567</p>
                </div>
              </div>
            </div>

            <div className="bg-beige/20 rounded-2xl p-6">
              <h3 className="font-playfair text-lg text-charcoal mb-2">Private Appointments</h3>
              <p className="text-charcoal/70 text-sm">
                For personalized consultations or to view exclusive pieces not available online, schedule a private appointment with our curators.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-ivory min-h-screen">
      <Toaster position="bottom-right" richColors />
      <BrowserRouter>
        <CartProvider>
          <Navigation />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/product/:id" element={<ProductDetailPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
          <Footer />
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
